import type { SidebarHive, SidebarItem } from '@alveolemtl/alveole-components';
import { GlobalSidebarItem, HiveSidebarItem, NavigationSidebar } from '@alveolemtl/alveole-components';
import { type OnProfileUpdate } from '@alveolemtl/alveole-components/dist/src/components/widgets/NavigationSidebar/ProfileDialog/ProfileDialog.js';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import type { Location } from 'history';
import { usePostHog } from 'posthog-js/react';
import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { generatePath, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useAdmin } from '@/providers/AdminProvider.js';
import { UPDATE_USER } from '@/services/user.service.js';

import {
  GetCurrentUserDocument,
  GetPageHomeDocument,
  Language as GraphqlLanguage,
  ListOwnedPagesDocument,
  type OwnedPage,
} from '../__generated__/graphql.js';
import { GrovePath } from '../config/paths.js';
import { myhiveLocalization, MyHiveLocalizationKey } from '../localization/my-hive.js';
import { useCurrentUser } from '../providers/CurrentUserProvider.js';
import { LanguageContext, languages } from '../providers/LanguageProvider.js';
import type { Language } from '../utils/localization.js';

const sidebarItemByPath: Partial<Record<GrovePath, SidebarItem>> = {
  [GrovePath.Reporting]: GlobalSidebarItem.Reporting,
  [GrovePath.PortfolioDashboard]: GlobalSidebarItem.PortfolioDashboard,
  [GrovePath.Asset]: HiveSidebarItem.MyHive,
  [GrovePath.AssetDownloads]: HiveSidebarItem.Downloads,
  // [GrovePath.AssetGallery]: HiveSidebarItem.Gallery,
  [GrovePath.AssetHoneyLabel]: HiveSidebarItem.HoneyLabel,
  [GrovePath.AssetIntegrations]: HiveSidebarItem.Integrations,
  [GrovePath.AssetPreferences]: HiveSidebarItem.Preferences,
  [GrovePath.AssetWorkshops]: HiveSidebarItem.Workshops,
};

interface Props {
  children: JSX.Element;
}

const MOBILE_NAVBAR_HEIGHT = '52px';

export default function Sidebar({ children }: Props): React.JSX.Element {
  const { logout, isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const posthog = usePostHog();
  const { isAlveole } = useAdmin();

  const { data, loading: isAssetListLoading } = useQuery(ListOwnedPagesDocument, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  const { user } = useCurrentUser();

  const [fetchData] = useLazyQuery(GetPageHomeDocument, {
    fetchPolicy: 'cache-first',
  });

  const sidebarHives: SidebarHive[] = useMemo(
    () => data?.ownedPages.map(buildSidebarHive).sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [data],
  );
  const [extraHive, setExtraHive] = useState<SidebarHive | null>();

  const [updateUser] = useMutation(UPDATE_USER, {
    context: {
      headers: {
        'apollo-require-preflight': true,
      },
    },
  });
  const handleUserUpdate: OnProfileUpdate = async (userUpdates) => {
    // eslint-disable-next-line @typescript-eslint/await-thenable
    const avatarImageFile = await userUpdates.avatarFile;

    if (!user?.id) {
      return;
    }

    void updateUser({
      variables: {
        input: {
          id: user.id,
          firstName: userUpdates.givenName,
          lastName: userUpdates.familyName,
          tagline: userUpdates.tagline,
          bio: userUpdates.beekeeperBio,
          avatarImageFile,
        },
      },
      refetchQueries: [GetCurrentUserDocument, GetPageHomeDocument],
    });
  };

  const handleLanguageChange = async (newLanguage: Language): Promise<void> => {
    setLanguage(newLanguage);
    if (!user?.id) {
      return;
    }
    await updateUser({
      variables: {
        input: {
          id: user.id,
          language: newLanguage === 'fr' ? GraphqlLanguage.Fr : GraphqlLanguage.En,
        },
      },
      refetchQueries: [GetCurrentUserDocument, GetPageHomeDocument],
    });
  };

  const assetSlug = getAssetSlug();
  const currentPathMatch =
    matchPath({ path: GrovePath.Asset }, location.pathname) ??
    matchPath({ path: GrovePath.AssetPreferences }, location.pathname) ??
    matchPath({ path: GrovePath.AssetWorkshops }, location.pathname) ??
    matchPath({ path: GrovePath.AssetDownloads }, location.pathname) ??
    // matchPath({ path: GrovePath.AssetGallery }, location.pathname) ??
    matchPath({ path: GrovePath.AssetHoneyLabel }, location.pathname) ??
    matchPath({ path: GrovePath.AssetIntegrations, end: true }, location.pathname);
  useEffect(() => {
    async function fetchPageData(): Promise<void> {
      if (isAssetListLoading) {
        return;
      }
      if (isAlveole && assetSlug && currentPathMatch) {
        const currentSlugMatchInSidebar = sidebarHives.find((hive) => hive.myHiveUrl === assetSlug);
        if (!currentSlugMatchInSidebar) {
          const pageData = await fetchData({
            variables: {
              slug: assetSlug,
            },
          });
          if (!pageData.data?.page) {
            return;
          }
          const pageInSidebar = sidebarHives.find((hive) => hive.id === pageData.data?.page.id);

          if (pageInSidebar) {
            // redirect to current slug.
            navigate(generatePath(currentPathMatch.pattern.path, { assetSlug: pageInSidebar.myHiveUrl ?? '' }));
            return;
          }
          setExtraHive({
            id: pageData.data.page.id,
            name: pageData.data.page.title,
            myHiveUrl: assetSlug,
            isReportingEnabled: pageData.data.page.isReportingEnabled,
          });
        } else {
          setExtraHive(null);
        }
      } else {
        setExtraHive(null);
      }
    }

    void fetchPageData();
  }, [assetSlug, isAlveole, sidebarHives, isAssetListLoading]);
  const allSidebarHives = extraHive ? [extraHive, ...sidebarHives] : sidebarHives;

  const someHiveHasRerportingEnabled: boolean = !!sidebarHives.filter((item) => item.isReportingEnabled).length;
  const selectedSidebarItem: SidebarItem | undefined = selectedSidebarItemFromLocation(location);
  function getAssetId(): string | undefined {
    const match =
      matchPath({ path: GrovePath.Asset }, location.pathname) ??
      matchPath({ path: GrovePath.AssetDownloads }, location.pathname) ??
      // matchPath({ path: GrovePath.AssetGallery }, location.pathname) ??
      matchPath({ path: GrovePath.AssetHoneyLabel }, location.pathname) ??
      matchPath({ path: GrovePath.AssetIntegrations, end: true }, location.pathname) ??
      matchPath({ path: GrovePath.AssetPreferences }, location.pathname) ??
      matchPath({ path: GrovePath.AssetWorkshops }, location.pathname);
    for (const hive of allSidebarHives) {
      if (hive.myHiveUrl === match?.params.assetSlug) {
        return hive.id;
      }
    }
    return undefined;
  }

  const selectedHiveId: string | undefined =
    selectedSidebarItem === HiveSidebarItem.MyHive ||
    selectedSidebarItem === HiveSidebarItem.Workshops ||
    selectedSidebarItem === HiveSidebarItem.Downloads ||
    // selectedSidebarItem === HiveSidebarItem.Gallery ||
    selectedSidebarItem === HiveSidebarItem.HoneyLabel ||
    selectedSidebarItem === HiveSidebarItem.Integrations ||
    selectedSidebarItem === HiveSidebarItem.Preferences ||
    selectedSidebarItem === HiveSidebarItem.Root ||
    selectedSidebarItem === HiveSidebarItem.Chat
      ? getAssetId()
      : undefined;

  return (
    <div style={{ display: 'flex' }}>
      <Box data-tour="sidebar" sx={{ flexShrink: 0, '@media print': { display: 'none' } }}>
        <ErrorBoundary fallback={<Fragment />}>
          <NavigationSidebar
            sendPasswordResetEmail={async () => {}}
            isLoading={isAssetListLoading}
            reportingMenuEnabled={someHiveHasRerportingEnabled}
            onHiveSidebarItemClick={(hiveSidebarItem: HiveSidebarItem, hive: SidebarHive) => {
              switch (hiveSidebarItem) {
                // case HiveSidebarItem.Gallery:
                //   navigate(generatePath(GrovePath.AssetGallery, { assetSlug: hive.myHiveUrl ?? '' }));
                //   break;
                case HiveSidebarItem.HoneyLabel:
                  navigate(generatePath(GrovePath.AssetHoneyLabel, { assetSlug: hive.myHiveUrl ?? '' }));
                  break;
                case HiveSidebarItem.Integrations:
                  navigate(generatePath(GrovePath.AssetIntegrations, { assetSlug: hive.myHiveUrl ?? '' }));
                  break;
                case HiveSidebarItem.Preferences:
                  navigate(generatePath(GrovePath.AssetPreferences, { assetSlug: hive.myHiveUrl ?? '' }));
                  break;
                case HiveSidebarItem.Workshops:
                  navigate(generatePath(GrovePath.AssetWorkshops, { assetSlug: hive.myHiveUrl ?? '' }));
                  break;
                case HiveSidebarItem.Downloads:
                  navigate(generatePath(GrovePath.AssetDownloads, { assetSlug: hive.myHiveUrl ?? '' }));
                  break;
                case HiveSidebarItem.MyHive:
                case HiveSidebarItem.Root:
                  navigate(generatePath(GrovePath.Asset, { assetSlug: hive.myHiveUrl ?? '' }));
                  break;
                default:
                  break;
              }
            }}
            onGlobalSidebarItemClick={(item) => {
              switch (item) {
                case GlobalSidebarItem.SignOut:
                  void logout({ logoutParams: { returnTo: `${window.location.origin}` } });
                  posthog.reset();
                  break;
                case GlobalSidebarItem.Reporting:
                  navigate(GrovePath.Reporting);
                  break;
                case GlobalSidebarItem.PortfolioDashboard:
                  navigate(GrovePath.PortfolioDashboard);
                  break;
                default:
                  break;
              }
            }}
            hives={allSidebarHives}
            user={{
              name: `${user?.firstName} ${user?.lastName}`,
              tagline: user?.tagline ?? undefined,
              avatarUrl: user?.avatarUrl ?? undefined,
              email: user?.email ?? '',
              isBeekeeper: true,
              // user?.getCurrentUser.role === 'BEEKEEPER' ||
              // user?.role === 'STAFF' ||
              // user?.role === 'PARTNER',
              givenName: user?.firstName ?? '',
              familyName: user?.lastName ?? '',
              beekeeperBio: user?.bio ?? '',
            }}
            onProfileUpdate={handleUserUpdate}
            languages={languages}
            selectedHiveId={selectedHiveId}
            selectedSidebarItem={selectedSidebarItem}
            selectedLanguageCode={language}
            onLanguageChange={(newLanguage) => {
              void handleLanguageChange(newLanguage as Language);
            }}
            externalLinks={[
              { label: language === 'fr' ? 'Site web' : 'Website', url: 'https://www.alveole.buzz' },
              { label: language === 'fr' ? 'Blogue' : 'Blog', url: 'https://www.alveole.buzz/blog/' },
              { label: 'Faq', url: t[MyHiveLocalizationKey.FaqLink] },
            ]}
          />
        </ErrorBoundary>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          pt: { xs: MOBILE_NAVBAR_HEIGHT, md: 0 },
          minHeight: '100vh',
          backgroundColor: '#F0ECE5',
          width: '100%',
        }}
        key={location.pathname}
        data-tour="main-content"
      >
        {children}
      </Box>
    </div>
  );
}

const buildSidebarHive = (page: OwnedPage): SidebarHive => ({
  id: page.id,
  name: page.title,
  myHiveUrl: page.slug ?? 'not-found',
  isReportingEnabled: page.isReportingEnabled,
});

function getAssetSlug(): string | undefined {
  const match =
    matchPath({ path: GrovePath.Asset }, location.pathname) ??
    matchPath({ path: GrovePath.AssetDownloads }, location.pathname) ??
    // matchPath({ path: GrovePath.AssetGallery }, location.pathname) ??
    matchPath({ path: GrovePath.AssetHoneyLabel }, location.pathname) ??
    matchPath({ path: GrovePath.AssetIntegrations, end: true }, location.pathname) ??
    matchPath({ path: GrovePath.AssetPreferences }, location.pathname) ??
    matchPath({ path: GrovePath.AssetWorkshops }, location.pathname);
  return match?.params.assetSlug;
}

function selectedSidebarItemFromLocation(location: Location): SidebarItem | undefined {
  for (const [path, sidebarItem] of Object.entries(sidebarItemByPath)) {
    if (matchPath(path, location.pathname)) {
      return sidebarItem;
    }
  }
  return undefined;
}
